import {
  deleteDatasetMonitor,
  listDatasetMonitors,
  runDatasetMonitor,
  setDatasetMonitorEnabled,
} from "@/features/data-curation/api/curation";
import { useShowChatNotes } from "@/features/data-curation/hooks/useCuration";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

const monitorsKey = (datasetId: string | null) => ["dataset-monitors", datasetId] as const;

/** The monitors of a dataset; a running one is polled often, so that its end shows soon. */
export const useDatasetMonitors = (datasetId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: monitorsKey(datasetId),
    queryFn: () => listDatasetMonitors(datasetId!),
    enabled: enabled && !!datasetId,
    refetchInterval: (query) =>
      query.state.data?.some((monitor) => monitor.running) ? 5_000 : 30_000,
  });

/** Refetch the monitors, e.g. when the dataset's agent starts or ends a turn. */
export const useRefreshDatasetMonitors = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  return useCallback(
    () => void queryClient.invalidateQueries({ queryKey: monitorsKey(datasetId) }),
    [datasetId, queryClient],
  );
};

const useMonitorMutation = <T>(
  datasetId: string | null,
  mutationFn: (variables: T) => Promise<unknown>,
  { chatNote = false }: { chatNote?: boolean } = {},
) => {
  const refresh = useRefreshDatasetMonitors(datasetId);
  const showChatNotes = useShowChatNotes(datasetId);
  return useMutation({
    mutationFn,
    onSuccess: () => {
      if (chatNote) showChatNotes();
    },
    onSettled: refresh,
  });
};

export const useSetDatasetMonitorEnabled = (datasetId: string | null) =>
  useMonitorMutation(
    datasetId,
    ({ monitorId, enabled }: { monitorId: string; enabled: boolean }) =>
      setDatasetMonitorEnabled(datasetId!, monitorId, enabled),
    { chatNote: true },
  );

export const useDeleteDatasetMonitor = (datasetId: string | null) =>
  useMonitorMutation(
    datasetId,
    (monitorId: string) => deleteDatasetMonitor(datasetId!, monitorId),
    { chatNote: true },
  );

export const useRunDatasetMonitor = (datasetId: string | null) =>
  useMonitorMutation(datasetId, (monitorId: string) =>
    runDatasetMonitor(datasetId!, monitorId),
  );
